import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/components/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 rounded-md",
  {
    variants: {
      variant: {
        default:
          "bg-[hsl(var(--sidebar-background))] text-[hsl(var(--color-primary-foreground))] shadow hover:opacity-90",
        secondary:
          "bg-[hsl(var(--color-secondary))] text-[hsl(var(--color-secondary-foreground))] hover:opacity-90",
        outline:
          "border bg-transparent hover:bg-[hsl(var(--color-accent))] hover:text-[hsl(var(--color-accent-foreground))] border-[hsl(var(--color-input))]",
        ghost:
          "hover:bg-[hsl(var(--color-accent))] hover:text-[hsl(var(--color-accent-foreground))]",
        destructive:
          "bg-[hsl(var(--color-destructive))] text-[hsl(var(--color-destructive-foreground))] hover:opacity-90",
        link: "text-[hsl(var(--color-primary))] underline-offset-4 hover:underline",
        success:
          "bg-[hsl(var(--color-success))] text-[hsl(var(--color-success-foreground))] hover:opacity-90",
      },
      size: {
        default: "h-9 rounded-md px-4 py-2",
        sm: "h-8 rounded-md px-3",
        lg: "h-10 rounded-md px-8",
        xl: "h-12 rounded-md px-10",  
        xxl: "h-14 rounded-md px-12",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  snaplandId?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", snaplandId, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        type={type}
        data-snapland-id={snaplandId || 'button-missing-id'}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };

